import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Plus, RefreshCw, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui-kit/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import { useAdminTemplatesUiStore } from '../store/use-admin-templates-ui-store';

/**
 * Split-button group rendered inside AdminTopbar for the
 * /admin/templates route on mobile. The primary action ("Add Template")
 * is the left half; the right half is a chevron trigger whose dropdown
 * surfaces Refresh + Import.
 *
 * Refresh invalidates the React Query cache for ['templates'], so both
 * the paginated table query and the unfiltered stats query refetch in
 * sync with the in-page Refresh button (which uses refetch()).
 */
export function AdminTemplatesTopbarActions() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const openImportDialog = useAdminTemplatesUiStore((s) => s.openImportDialog);
  const isFetching = useIsFetching({ queryKey: ['templates'] }) > 0;

  return (
    <div className="inline-flex items-stretch rounded-md shadow-xs">
      <Button asChild size="sm" variant="primary" className="rounded-r-none">
        <Link to="/admin/templates/new" className="flex items-center gap-1">
          <Plus className="size-4" />
          {t('ADMIN_TEMPLATES_ACTIONS_ADDTEMPLATE')}
        </Link>
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="primary"
            className="rounded-l-none border-l border-white/20 px-2"
            aria-label="More actions"
          >
            <ChevronDown className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuItem
            onSelect={() => queryClient.invalidateQueries({ queryKey: ['templates'] })}
          >
            <RefreshCw className={`size-4 ${isFetching ? 'animate-spin' : ''}`} />
            {t('ADMIN_TEMPLATES_ACTIONS_REFRESH')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={openImportDialog}>
            <Upload className="size-4" />
            Import
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
